import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from 'react';
import { ActiveSmartGuide } from './types';

interface SmartGuideContextValue {
  activeGuides: ActiveSmartGuide[];
  setActiveGuides: (guides: ActiveSmartGuide[]) => void;
  clearGuides: () => void;
}

const SmartGuideContext = createContext<SmartGuideContextValue | null>(null);

function areGuidesEqual(a: ActiveSmartGuide[], b: ActiveSmartGuide[]): boolean {
  if (a.length !== b.length) return false;
  return a.every(
    (guide, i) =>
      guide.type === b[i]?.type && guide.position === b[i]?.position,
  );
}

export function SmartGuideProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const [activeGuides, setActiveGuidesState] = useState<ActiveSmartGuide[]>([]);
  const prevGuidesRef = useRef<ActiveSmartGuide[]>([]);

  const setActiveGuides = useCallback((guides: ActiveSmartGuide[]) => {
    if (!areGuidesEqual(guides, prevGuidesRef.current)) {
      prevGuidesRef.current = guides;
      setActiveGuidesState(guides);
    }
  }, []);

  const clearGuides = useCallback(() => {
    if (prevGuidesRef.current.length > 0) {
      prevGuidesRef.current = [];
      setActiveGuidesState([]);
    }
  }, []);

  return (
    <SmartGuideContext.Provider
      value={{ activeGuides, setActiveGuides, clearGuides }}
    >
      {children}
    </SmartGuideContext.Provider>
  );
}

export function useSmartGuides() {
  const context = useContext(SmartGuideContext);
  if (!context) {
    throw new Error('useSmartGuides must be used within SmartGuideProvider');
  }
  return context;
}

export function useSmartGuidesOptional() {
  return useContext(SmartGuideContext);
}
